import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';

interface DocumentTemplateUnavailableProps {
  /** Where the user goes when they back out — EOC details or the therapist dashboard. */
  onBack: () => void;
  backLabel?: string;
  /**
   * Why the document can't be opened:
   * - `unconfigured` (default): a native form type whose template has no reform_id.
   * - `legacy`: a document written in the retired Jotform system, which can no longer be edited.
   */
  reason?: 'unconfigured' | 'legacy';
}

/**
 * BRK-1921: shown when a document cannot be rendered natively because its form template has no
 * reform_id configured for this facility. Previously these cases fell through to a JotForm iframe
 * (which loaded the legacy form) or to an empty iframe (a silent blank pane). Both hid a
 * misconfiguration from the user and from us, so the state is now explicit.
 *
 * The fix is data, not user action — reform_id has to be backfilled on the facility's template —
 * so the copy points at support rather than offering a retry.
 *
 * BRK-2129: Jotform is retired, so documents written in it can't be edited at all. They get the
 * `legacy` copy instead.
 */
const DocumentTemplateUnavailable = ({
  onBack,
  backLabel = 'Go Back',
  reason = 'unconfigured',
}: DocumentTemplateUnavailableProps) => (
  <Alert
    severity="error"
    sx={{ '& .MuiAlert-message': { width: '100%' } }}
  >
    <AlertTitle>This document can’t be opened</AlertTitle>
    <Stack gap={2}>
      <Typography variant="body2">
        {reason === 'legacy'
          ? 'It was created in a form system that has been retired, so it can no longer be opened for editing. No data has been lost. Please contact support if it needs to be changed.'
          : 'Its form template isn’t fully configured for this facility, so it can’t be opened for editing. No data has been lost. Please contact support so the template can be corrected.'}
      </Typography>
      <Button
        onClick={onBack}
        variant="outlined"
        size="small"
        sx={{ alignSelf: 'flex-start' }}
      >
        {backLabel}
      </Button>
    </Stack>
  </Alert>
);

export default DocumentTemplateUnavailable;
